import React, { useState, useEffect } from 'react';
import { CreditCard, Save, Trash2, Plus, Eye, EyeOff, RefreshCw, AlertTriangle, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

export function AdminSubscriptions() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      // Get ALL plans including hidden ones (admin endpoint)
      const res = await api.get('/admin/subscription-plans');
      setPlans(res.data);
    } catch (error) {
      // Fallback to public endpoint
      try {
        const res = await api.get('/subscriptions/tiers');
        setPlans(res.data.map(p => ({
          tier_id: p.tier,
          name: p.name,
          price: p.price,
          features: p.features,
          is_popular: p.popular,
          is_hidden: false,
          requires_application: p.requires_application,
          unlocks_all_chapters: p.unlocks_all_chapters
        })));
      } catch (e) {
        toast.error('Failed to load plans');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = (index, field, value) => {
    const updated = [...plans];
    updated[index] = { ...updated[index], [field]: value };
    setPlans(updated);
  };

  const handleFeatureChange = (planIndex, featureIndex, value) => {
    const updated = [...plans];
    updated[planIndex].features[featureIndex] = value;
    setPlans(updated);
  };

  const addFeature = (planIndex) => {
    const updated = [...plans];
    updated[planIndex].features = [...(updated[planIndex].features || []), ''];
    setPlans(updated);
  };

  const removeFeature = (planIndex, featureIndex) => {
    const updated = [...plans];
    updated[planIndex].features = updated[planIndex].features.filter((_, i) => i !== featureIndex);
    setPlans(updated);
  };

  const savePlan = async (plan) => {
    setSaving(true);
    try {
      await api.put(`/admin/subscription-plans/${plan.tier_id}`, plan);
      toast.success(`${plan.name} plan updated!`);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const resetAllPlans = async () => {
    if (!window.confirm('⚠️ RESET ALL PLANS?\n\nThis will DELETE all existing plans and create:\n• Free ($0)\n• Premium ($4/month)\n• Creator (Apply Only)\n• Contributor (Hidden)\n\nThis cannot be undone!')) {
      return;
    }
    
    setResetting(true);
    try {
      const res = await api.post('/subscriptions/plans/reset');
      toast.success(res.data.message);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const movePlan = async (index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === plans.length - 1)
    ) {
      return;
    }

    setReordering(true);
    const newPlans = [...plans];
    const swapIndex = index + direction;
    
    // Swap the plans
    [newPlans[index], newPlans[swapIndex]] = [newPlans[swapIndex], newPlans[index]];
    
    // Update sort_order values
    const reorderData = newPlans.map((plan, i) => ({
      tier_id: plan.tier_id,
      sort_order: i
    }));

    try {
      await api.post('/admin/subscription-plans/reorder', { plans: reorderData });
      setPlans(newPlans);
      toast.success('Plan order updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reorder');
    } finally {
      setReordering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-subscriptions">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-teko">Subscription Plans</h2>
          <p className="text-sm text-[var(--text-secondary)]">Manage pricing and features</p>
        </div>
        <button
          onClick={resetAllPlans}
          disabled={resetting}
          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
        >
          {resetting ? <RefreshCw size={16} className="animate-spin" /> : <AlertTriangle size={16} />}
          Reset All Plans
        </button>
      </div>

      {/* Plans */}
      <div className="grid gap-6">
        {plans.map((plan, planIndex) => (
          <div 
            key={plan.tier_id} 
            className={`bg-[var(--surface)] border rounded-xl p-6 ${plan.is_hidden ? 'border-yellow-500/50 opacity-75' : 'border-[var(--border)]'}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {/* Reorder Controls */}
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => movePlan(planIndex, -1)}
                    disabled={planIndex === 0 || reordering}
                    className="p-1 hover:bg-[var(--background)] rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    onClick={() => movePlan(planIndex, 1)}
                    disabled={planIndex === plans.length - 1 || reordering}
                    className="p-1 hover:bg-[var(--background)] rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ArrowDown size={16} />
                  </button>
                </div>
                
                <CreditCard size={24} className={plan.is_popular ? 'text-[var(--primary)]' : 'text-[var(--text-secondary)]'} />
                <div>
                  <input
                    type="text"
                    value={plan.name}
                    onChange={(e) => handlePlanChange(planIndex, 'name', e.target.value)}
                    className="text-xl font-teko bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--primary)] focus:outline-none"
                  />
                  <p className="text-xs text-[var(--text-secondary)]">ID: {plan.tier_id}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {plan.is_hidden && (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-500 text-xs rounded">Hidden</span>
                )}
                {plan.is_popular && (
                  <span className="px-2 py-1 bg-[var(--primary)]/20 text-[var(--primary)] text-xs rounded">Popular</span>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Price */}
              <div>
                <label className="block text-xs text-[var(--text-secondary)] mb-1">Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={plan.price}
                  onChange={(e) => handlePlanChange(planIndex, 'price', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                />
              </div>

              {/* Trial Days */}
              <div>
                <label className="block text-xs text-[var(--text-secondary)] mb-1">Free Trial Days</label>
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={plan.trial_days || 0}
                  onChange={(e) => handlePlanChange(planIndex, 'trial_days', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                />
              </div>

              {/* Billing Interval */}
              <div>
                <label className="block text-xs text-[var(--text-secondary)] mb-1">Billing Interval</label>
                <select
                  value={plan.billing_interval || 'month'}
                  onChange={(e) => handlePlanChange(planIndex, 'billing_interval', e.target.value)}
                  className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>

              {/* Billing Count */}
              <div>
                <label className="block text-xs text-[var(--text-secondary)] mb-1">Every X Intervals</label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={plan.billing_interval_count || 1}
                  onChange={(e) => handlePlanChange(planIndex, 'billing_interval_count', parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                />
              </div>
            </div>

            {/* Billing Summary */}
            {plan.price > 0 && (
              <div className="mb-4 p-3 bg-[var(--background)] rounded-lg border border-[var(--border)]">
                <p className="text-sm">
                  <span className="text-[var(--text-secondary)]">Billing: </span>
                  <span className="font-medium">
                    ${plan.price.toFixed(2)} every {plan.billing_interval_count || 1} {plan.billing_interval || 'month'}(s)
                  </span>
                  {plan.trial_days > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                      {plan.trial_days}-day free trial
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Toggles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={plan.is_popular}
                  onChange={(e) => handlePlanChange(planIndex, 'is_popular', e.target.checked)}
                  className="w-4 h-4"
                />
                Popular
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={plan.is_hidden}
                  onChange={(e) => handlePlanChange(planIndex, 'is_hidden', e.target.checked)}
                  className="w-4 h-4"
                />
                Hidden
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={plan.requires_application}
                  onChange={(e) => handlePlanChange(planIndex, 'requires_application', e.target.checked)}
                  className="w-4 h-4"
                />
                Requires App
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={plan.unlocks_all_chapters}
                  onChange={(e) => handlePlanChange(planIndex, 'unlocks_all_chapters', e.target.checked)}
                  className="w-4 h-4"
                />
                Unlocks All
              </label>
            </div>

            {/* Features */}
            <div className="mb-4">
              <label className="block text-xs text-[var(--text-secondary)] mb-2">Features</label>
              <div className="space-y-2">
                {(plan.features || []).map((feature, featureIndex) => (
                  <div key={`${plan.tier_id}-feature-${featureIndex}`} className="flex gap-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureChange(planIndex, featureIndex, e.target.value)}
                      className="flex-1 px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm"
                      placeholder="Feature description..."
                    />
                    <button
                      onClick={() => removeFeature(planIndex, featureIndex)}
                      className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addFeature(planIndex)}
                  className="text-sm text-[var(--primary)] hover:underline flex items-center gap-1"
                >
                  <Plus size={14} /> Add feature
                </button>
              </div>
            </div>

            {/* Save Button */}
            <button
              onClick={() => savePlan(plan)}
              disabled={saving}
              className="w-full py-2 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save size={16} />
              Save {plan.name} Plan
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminSubscriptions;
